/**
 * IPFS Service for uploading NFT files and metadata
 */

interface IPFSUploadResponse {
  success: boolean;
  hash?: string;
  url?: string;
  error?: string;
}

interface NFTMetadata {
  name: string;
  description: string;
  image: string;
  attributes?: Array<{
    trait_type: string;
    value: string | number;
    rarity?: number;
    display_type?: 'string' | 'number' | 'boost_number' | 'boost_percentage' | 'date';
  }>;
  external_url?: string;
  background_color?: string;
  animation_url?: string;
  youtube_url?: string;
}

class IPFSService {
  private pinataApiKey: string;
  private pinataSecretKey: string;
  private pinataGateway: string;

  constructor() {
    // These should be set in your environment variables
    this.pinataApiKey = import.meta.env.VITE_PINATA_API_KEY || '';
    this.pinataSecretKey = import.meta.env.VITE_PINATA_API_SECRET || '';
    this.pinataGateway = import.meta.env.VITE_PINATA_GATEWAY || 'https://gateway.pinata.cloud';
  }

  /**
   * Upload a file to IPFS via Pinata
   */
  async uploadFile(file: File, name?: string): Promise<IPFSUploadResponse> {
    try {
      if (!this.pinataApiKey || !this.pinataSecretKey) {
        throw new Error('Pinata API credentials not configured');
      }

      const formData = new FormData();
      formData.append('file', file);
      
      const metadata = JSON.stringify({
        name: name || file.name,
        keyvalues: {
          type: 'nft-asset',
          uploadedAt: new Date().toISOString()
        }
      });
      formData.append('pinataMetadata', metadata);

      const response = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
        method: 'POST',
        headers: {
          'pinata_api_key': this.pinataApiKey,
          'pinata_secret_api_key': this.pinataSecretKey,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        hash: result.IpfsHash,
        url: `${this.pinataGateway}/ipfs/${result.IpfsHash}`
      };
    } catch (error) {
      console.error('IPFS file upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Upload multiple files to IPFS
   */
  async uploadFiles(files: File[]): Promise<IPFSUploadResponse[]> {
    const uploadPromises = files.map((file, index) => 
      this.uploadFile(file, `${file.name.split('.')[0]}_${index}`)
    );
    
    return Promise.all(uploadPromises);
  }

  /**
   * Upload JSON metadata to IPFS
   */
  async uploadMetadata(metadata: NFTMetadata): Promise<IPFSUploadResponse> {
    try {
      if (!this.pinataApiKey || !this.pinataSecretKey) {
        throw new Error('Pinata API credentials not configured');
      }

      const response = await fetch('https://api.pinata.cloud/pinning/pinJSONToIPFS', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'pinata_api_key': this.pinataApiKey,
          'pinata_secret_api_key': this.pinataSecretKey,
        },
        body: JSON.stringify({
          pinataContent: metadata,
          pinataMetadata: {
            name: `${metadata.name}_metadata`,
            keyvalues: {
              type: 'nft-metadata',
              uploadedAt: new Date().toISOString()
            }
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        hash: result.IpfsHash,
        url: `${this.pinataGateway}/ipfs/${result.IpfsHash}`
      };
    } catch (error) {
      console.error('IPFS metadata upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Complete NFT upload process: file + metadata
   */
  async uploadNFT(
    file: File, 
    metadata: Omit<NFTMetadata, 'image'>
  ): Promise<{ fileUpload: IPFSUploadResponse; metadataUpload: IPFSUploadResponse }> {
    // First upload the file
    const fileUpload = await this.uploadFile(file, metadata.name);
    
    if (!fileUpload.success) {
      return {
        fileUpload,
        metadataUpload: { success: false, error: 'File upload failed' }
      };
    }

    // Then upload metadata with the file URL
    const completeMetadata: NFTMetadata = {
      ...metadata,
      image: fileUpload.url!
    };

    const metadataUpload = await this.uploadMetadata(completeMetadata);

    return { fileUpload, metadataUpload };
  }

  /**
   * Batch upload NFTs
   */
  async uploadBatchNFTs(
    files: File[],
    baseMetadata: Omit<NFTMetadata, 'image'> & { baseName?: string }
  ): Promise<Array<{ fileUpload: IPFSUploadResponse; metadataUpload: IPFSUploadResponse }>> {
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileName = file.name.split('.')[0];
      
      const metadata = {
        ...baseMetadata,
        name: `${baseMetadata.baseName || baseMetadata.name || 'NFT'} #${i + 1}`,
        description: `${baseMetadata.description} - ${fileName}`
      };

      const result = await this.uploadNFT(file, metadata);
      results.push(result);
    }

    return results;
  }

  /**
   * Get IPFS URL from hash
   */
  getIPFSUrl(hash: string): string {
    return `${this.pinataGateway}/ipfs/${hash}`;
  }

  /**
   * Test IPFS connection
   */
  async testConnection(): Promise<boolean> {
    try {
      if (!this.pinataApiKey || !this.pinataSecretKey) {
        return false;
      }

      const response = await fetch('https://api.pinata.cloud/data/testAuthentication', {
        method: 'GET',
        headers: {
          'pinata_api_key': this.pinataApiKey,
          'pinata_secret_api_key': this.pinataSecretKey,
        },
      });

      return response.ok;
    } catch (error) {
      console.error('IPFS connection test failed:', error);
      return false;
    }
  }
}

export const ipfsService = new IPFSService();
export type { IPFSUploadResponse, NFTMetadata };
